import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuthStore } from '@/stores/authStore';
import { toast } from 'sonner';
import { generateSlug, getRedirectUrl } from '@/lib/dynamic-qr-utils';

export type ABTestType = 'design' | 'destination';
export type ABTestStatus = 'draft' | 'active' | 'paused' | 'completed';

export interface ABTestVariant {
  id: string;
  abTestId: string;
  variantName: string;
  qrCodeId?: string;
  shortSlug?: string;
  designSettings: Record<string, unknown>;
  destinationUrl?: string;
  scanCount: number;
  conversionCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ABTest {
  id: string;
  userId: string;
  qrCodeId?: string;
  name: string;
  testType: ABTestType;
  status: ABTestStatus;
  trafficSplit: Record<string, number>;
  startDate?: string;
  endDate?: string;
  winnerVariant?: string;
  successMetric: string;
  createdAt: string;
  updatedAt: string;
  variants?: ABTestVariant[];
}

interface CreateABTestInput {
  name: string;
  qrCodeId?: string;
  testType: ABTestType;
  trafficSplit: Record<string, number>;
  startDate?: string;
  endDate?: string;
  successMetric?: string;
  variants: {
    variantName: string;
    designSettings?: Record<string, unknown>;
    destinationUrl?: string;
  }[];
}

function mapDBToABTest(dbTest: Record<string, unknown>): ABTest {
  return {
    id: dbTest.id as string,
    userId: dbTest.user_id as string,
    qrCodeId: dbTest.qr_code_id as string | undefined,
    name: dbTest.name as string,
    testType: dbTest.test_type as ABTestType,
    status: dbTest.status as ABTestStatus,
    trafficSplit: dbTest.traffic_split as Record<string, number>,
    startDate: dbTest.start_date as string | undefined,
    endDate: dbTest.end_date as string | undefined,
    winnerVariant: dbTest.winner_variant as string | undefined,
    successMetric: dbTest.success_metric as string,
    createdAt: dbTest.created_at as string,
    updatedAt: dbTest.updated_at as string,
  };
}

function mapDBToVariant(dbVariant: Record<string, unknown>): ABTestVariant {
  return {
    id: dbVariant.id as string,
    abTestId: dbVariant.ab_test_id as string,
    variantName: dbVariant.variant_name as string,
    qrCodeId: dbVariant.qr_code_id as string | undefined,
    shortSlug: dbVariant.short_slug as string | undefined,
    designSettings: (dbVariant.design_settings as Record<string, unknown>) || {},
    destinationUrl: dbVariant.destination_url as string | undefined,
    scanCount: dbVariant.scan_count as number,
    conversionCount: dbVariant.conversion_count as number,
    createdAt: dbVariant.created_at as string,
    updatedAt: dbVariant.updated_at as string,
  };
}

export function useABTests() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  const { data: tests = [], isLoading } = useQuery({
    queryKey: ['ab-tests', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('ab_tests')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []).map((t) => mapDBToABTest(t as Record<string, unknown>));
    },
    enabled: !!user,
  });

  const { data: variants = [] } = useQuery({
    queryKey: ['ab-test-variants', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('ab_test_variants')
        .select('*')
        .order('variant_name', { ascending: true });

      if (error) throw error;
      return (data || []).map((v) => mapDBToVariant(v as Record<string, unknown>));
    },
    enabled: !!user,
  });

  // Combine tests with their variants
  const testsWithVariants = tests.map((test) => ({
    ...test,
    variants: variants.filter((v) => v.abTestId === test.id),
  }));

  const createTest = async (input: CreateABTestInput): Promise<ABTest> => {
    if (!user) throw new Error('Not authenticated');

    // Create the test
    const { data: testData, error: testError } = await supabase
      .from('ab_tests')
      .insert({
        user_id: user.id,
        qr_code_id: input.qrCodeId || null,
        name: input.name,
        test_type: input.testType,
        traffic_split: input.trafficSplit,
        start_date: input.startDate || null,
        end_date: input.endDate || null,
        success_metric: input.successMetric || 'scans',
        status: 'draft',
      })
      .select()
      .single();

    if (testError) throw testError;

    // Create variants
    const variantInserts = input.variants.map((v) => ({
      ab_test_id: testData.id,
      variant_name: v.variantName,
      short_slug: generateSlug(),
      design_settings: (v.designSettings || {}) as unknown,
      destination_url: v.destinationUrl || null,
    }));

    const { error: variantError } = await supabase
      .from('ab_test_variants')
      .insert(variantInserts as never[]);

    if (variantError) throw variantError;

    queryClient.invalidateQueries({ queryKey: ['ab-tests'] });
    queryClient.invalidateQueries({ queryKey: ['ab-test-variants'] });
    
    toast.success('A/B test created successfully!');
    return mapDBToABTest(testData as Record<string, unknown>);
  };

  const updateTestStatus = async (testId: string, status: ABTestStatus) => {
    const updates: Record<string, unknown> = { status };
    
    if (status === 'active' && !tests.find(t => t.id === testId)?.startDate) {
      updates.start_date = new Date().toISOString();
    }

    const { error } = await supabase
      .from('ab_tests')
      .update(updates)
      .eq('id', testId);

    if (error) throw error;

    queryClient.invalidateQueries({ queryKey: ['ab-tests'] });
    toast.success(`Test ${status === 'active' ? 'activated' : status === 'paused' ? 'paused' : 'updated'}!`);
  };

  const declareWinner = async (testId: string, winnerVariant: string) => {
    const { error } = await supabase
      .from('ab_tests')
      .update({
        status: 'completed',
        winner_variant: winnerVariant,
        end_date: new Date().toISOString(),
      })
      .eq('id', testId);

    if (error) throw error;

    queryClient.invalidateQueries({ queryKey: ['ab-tests'] });
    toast.success(`Variant ${winnerVariant} declared as winner!`);
  };

  const deleteTest = async (testId: string) => {
    const { error } = await supabase
      .from('ab_tests')
      .delete()
      .eq('id', testId);

    if (error) throw error;

    queryClient.invalidateQueries({ queryKey: ['ab-tests'] });
    queryClient.invalidateQueries({ queryKey: ['ab-test-variants'] });
    toast.success('A/B test deleted');
  };

  const updateTrafficSplit = async (testId: string, trafficSplit: Record<string, number>) => {
    const { error } = await supabase
      .from('ab_tests')
      .update({ traffic_split: trafficSplit })
      .eq('id', testId);

    if (error) throw error;

    queryClient.invalidateQueries({ queryKey: ['ab-tests'] });
    toast.success('Traffic split updated');
  };

  const activeTestsCount = tests.filter((t) => t.status === 'active').length;

  return {
    tests: testsWithVariants,
    isLoading,
    createTest,
    updateTestStatus,
    declareWinner,
    deleteTest,
    updateTrafficSplit,
    activeTestsCount,
  };
}

// Get variant scans for analytics
export function useABTestAnalytics(testId: string | null) {
  return useQuery({
    queryKey: ['ab-test-analytics', testId],
    queryFn: async () => {
      if (!testId) return null;

      // Get variants with their scans
      const { data: variants, error: variantError } = await supabase
        .from('ab_test_variants')
        .select('*')
        .eq('ab_test_id', testId);

      if (variantError) throw variantError;

      // Get scans per variant over time
      const variantIds = variants?.map((v) => v.id) || [];
      
      const { data: scans, error: scansError } = await supabase
        .from('qr_scans')
        .select('*')
        .in('ab_test_variant_id', variantIds)
        .order('scanned_at', { ascending: true });

      if (scansError) throw scansError;

      return {
        variants: (variants || []).map((v) => mapDBToVariant(v as Record<string, unknown>)),
        scans: scans || [],
      };
    },
    enabled: !!testId,
    refetchInterval: 60000, // Refresh every 60 seconds
  });
}

// Statistical significance calculation (chi-square test)
export function calculateSignificance(
  variantA: { scans: number; conversions?: number },
  variantB: { scans: number; conversions?: number }
): { significant: boolean; confidence: number; winner?: 'A' | 'B' } {
  const totalA = variantA.scans;
  const totalB = variantB.scans;
  
  if (totalA < 30 || totalB < 30) {
    return { significant: false, confidence: 0 };
  }

  // Simple comparison based on scan counts
  const total = totalA + totalB;
  const expectedA = total / 2;
  const expectedB = total / 2;

  // Chi-square calculation
  const chiSquare = 
    Math.pow(totalA - expectedA, 2) / expectedA +
    Math.pow(totalB - expectedB, 2) / expectedB;

  // Chi-square critical values: 3.84 (95%), 6.63 (99%)
  let confidence = 0;
  let significant = false;
  let winner: 'A' | 'B' | undefined;

  if (chiSquare >= 6.63) {
    confidence = 99;
    significant = true;
  } else if (chiSquare >= 3.84) {
    confidence = 95;
    significant = true;
  } else if (chiSquare >= 2.71) {
    confidence = 90;
  }

  if (significant) {
    winner = totalA > totalB ? 'A' : 'B';
  }

  return { significant, confidence, winner };
}
